import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { PageHeader, Banner, ExportButton, WideSection, ICONS } from '../components/ui';
import { ComboChart, SERIES_COLORS } from '../components/charts/BarCharts';
import ChartAxes, { dropOnChart } from '../components/charts/ChartAxes';
import ChartFrame from '../components/charts/ChartFrame';
import { exportToExcel } from '../utils/exportExcel';
import { displayDate, displayMonth } from '../utils/dates';
import { moneyWhole, hours as fmtHours } from '../utils/format';
import { presetRange, costRange, rateRange, monthEnd } from '../utils/reportDates';

// The measures, in fixed order. Colors follow the measure, never its rank.
const MEASURES = [
  { key: 'Hours', label: 'Hours', unit: 'hours', color: SERIES_COLORS[4] },
  { key: 'RateSheet', label: 'Rate Sheet Cost', unit: 'money', color: SERIES_COLORS[0] },
  { key: 'Cost', label: 'Actual Cost', unit: 'money', color: SERIES_COLORS[1] },
  { key: 'Billed', label: 'Invoiced', unit: 'money', color: SERIES_COLORS[2] },
  { key: 'Budget', label: 'Budget', unit: 'money', color: SERIES_COLORS[3] },
];
const GROUPS = [
  { key: 'J', label: 'Job' },
  { key: 'T', label: 'Task line' },
  { key: 'C', label: 'Task Line Type' },
  { key: 'P', label: 'Person' },
  { key: 'M', label: 'Project Manager' },
  { key: 'D', label: 'Director' },
  { key: 'S', label: 'Service' },
  { key: 'U', label: 'Sub-Service' },
];
// Which measures the over-time chart shows, and on which axis (left = bars,
// right = lines); remembered in this browser.
const AXES_KEY = 'reports.chartAxes';
const DEFAULT_AXES = { left: ['RateSheet', 'Cost', 'Billed', 'Budget'], right: ['Hours'] };
function loadAxes() {
  try {
    const a = JSON.parse(window.localStorage.getItem(AXES_KEY));
    if (a && Array.isArray(a.left) && Array.isArray(a.right)) return separateUnits(null, a);
  } catch (_) { /* default */ }
  return DEFAULT_AXES;
}
// Dollars and hours never share an axis: when both are charted, all dollar
// measures go on one axis and hours on the other. The measure just moved
// (prev -> next) keeps the axis it was put on; otherwise dollars stay on the
// side that has more of them.
const unitOf = (k) => (MEASURES.find((m) => m.key === k) || {}).unit;
function separateUnits(prev, next) {
  const keys = [...next.left, ...next.right];
  if (new Set(keys.map(unitOf)).size < 2) return next;
  const sideIn = (a, k) => (a.left.includes(k) ? 'left' : a.right.includes(k) ? 'right' : '');
  const moved = prev && keys.find((k) => sideIn(prev, k) !== sideIn(next, k));
  const count = (side) => next[side].filter((k) => unitOf(k) === 'money').length;
  const other = (side) => (side === 'left' ? 'right' : 'left');
  const moneySide = moved
    ? (unitOf(moved) === 'money' ? sideIn(next, moved) : other(sideIn(next, moved)))
    : (count('left') >= count('right') ? 'left' : 'right');
  const ordered = (unit) => MEASURES.map((m) => m.key).filter((k) => keys.includes(k) && unitOf(k) === unit);
  return { [moneySide]: ordered('money'), [other(moneySide)]: ordered('hours') };
}
// One axis's tick format: its measures' unit, or plain numbers when mixed.
function axisFormat(ms) {
  const units = new Set(ms.map((m) => m.unit));
  if (units.size === 1) return fmtMeasure(ms[0].unit);
  return (v, short) => (short && Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k` : Math.round(v).toLocaleString('en-US'));
}
function axisTitle(ms) {
  const units = new Set(ms.map((m) => m.unit));
  if (units.size !== 1) return '';
  return units.has('hours') ? 'Hours' : 'Dollars';
}

// Chart series for measures; values(key) gives a measure's numbers.
const chartSeries = (ms, values) => ms.map((m) => ({ key: m.key, label: m.label, color: m.color, format: fmtMeasure(m.unit), values: values(m.key) }));

const CATEGORIES = [
  { key: '', label: 'All task line types' },
  { key: 'L', label: 'License' },
  { key: 'S', label: 'Lump Sum' },
  { key: 'T', label: 'T&M' },
  { key: 'E', label: 'Expenses' },
  { key: 'C', label: 'Charges' },
  { key: 'O', label: 'Other' },
];
const TOP_N = [
  { key: '5', label: '5' },
  { key: '10', label: '10' },
  { key: '15', label: '15' },
  { key: '25', label: '25' },
  { key: '50', label: '50' },
  { key: 'all', label: 'All' },
];
const PERIODS = [
  { key: 'M', label: 'Month' },
  { key: 'Q', label: 'Quarter' },
  { key: 'Y', label: 'Year' },
  { key: 'A', label: 'Total' },
];
const RANGES = [
  { key: 'last12', label: 'Last 12 full months' },
  { key: 'last4q', label: 'Last 4 quarters' },
  { key: 'ytd', label: 'This year' },
  { key: 'lastYear', label: 'Last year' },
  { key: 'months', label: 'Months…' },
  { key: 'days', label: 'Dates…' },
  { key: 'all', label: 'All time' },
];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// The settings a saved report (favorite) keeps.
const SETTING_KEYS = ['range', 'months', 'custom', 'period', 'groupBy', 'measures', 'job', 'pm', 'director',
  'service', 'subService', 'category', 'person', 'overhead', 'jobState', 'taskState', 'axes', 'rankBy', 'topN'];
// One key per (grouping) / (period, grouping) row.
const groupKey = (r) => [r.jobNo, r.jobTaskNo, r.category, r.pmNo, r.directorNo, r.service, r.subService, r.resourceNo].join('|');
const rowKey = (r) => `${r.period}|${groupKey(r)}`;
const JOB_STATUS = { 0: 'Planning', 1: 'Quote', 2: 'Open', 3: 'Completed' };

// From/to ('YYYY-MM-DD', '' = open-ended) for the date choice. months =
// { from, to } as 'YYYY-MM' (whole months); custom = exact dates.
function rangeDates(key, today, months, custom) {
  if (key === 'days') return custom;
  if (key === 'months') {
    const [ty, tm] = (months.to || '').split('-').map(Number);
    return { from: months.from ? `${months.from}-01` : '', to: ty ? monthEnd(ty, tm) : '' };
  }
  return presetRange(key, today);
}

function periodLabel(p, period) {
  if (!p) return 'Total';
  const [y, m] = p.split('-').map(Number);
  if (period === 'Y') return String(y);
  if (period === 'Q') return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
  return displayMonth(p);
}

const fmtMeasure = (unit) => (v, short) => {
  if (unit === 'hours') return short ? Math.round(v).toLocaleString('en-US') : fmtHours(v);
  if (short && Math.abs(v) >= 1000) return `$${(v / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`;
  return moneyWhole(Math.round(v * 100) / 100);
};

// Analytics > Reports: hours, rate-sheet value, actual cost, billed and budget
// for the user's jobs (PM / Director; all jobs for Executive), by period and
// grouping, with charts, a table, and Excel export.
export default function ReportsPage() {
  const [opts, setOpts] = useState(null);
  const [range, setRange] = useState('last12');
  const [months, setMonths] = useState({ from: '', to: '' }); // 'YYYY-MM'
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [period, setPeriod] = useState('M');
  const [groupBy, setGroupBy] = useState(['J']);
  const [measures, setMeasures] = useState(MEASURES.map((m) => m.key));
  const [job, setJob] = useState('');
  const [pm, setPm] = useState('');
  const [director, setDirector] = useState(''); // task-line Director (else the job's)
  const [service, setService] = useState('');
  const [subService, setSubService] = useState(''); // '-' = none
  const [category, setCategory] = useState('');
  const [person, setPerson] = useState('');
  const [overhead, setOverhead] = useState('N');
  const [jobState, setJobState] = useState('*');
  const [taskState, setTaskState] = useState('*');
  const [result, setResult] = useState(null); // { rows, ran: { period, groupBy, from, to } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rankBy, setRankBy] = useState('RateSheet');
  const [sort, setSort] = useState({ key: null, dir: 1 }); // table sort; null key = natural order
  const [axes, setAxesState] = useState(loadAxes);
  const setAxes = (a) => {
    const next = separateUnits(axes, a);
    setAxesState(next);
    try { window.localStorage.setItem(AXES_KEY, JSON.stringify(next)); } catch (_) { /* not remembered */ }
  };
  const [topN, setTopN] = useState('15');
  const [showAllPms, setShowAllPms] = useState(false);
  const [showAllDirectors, setShowAllDirectors] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [favId, setFavId] = useState('');

  useEffect(() => {
    api.getAnalyticsOptions().then(setOpts).catch((e) => setError((e && e.message) || 'Failed to load'));
    api.getReportFavorites().then((r) => setFavorites((r && r.favorites) || [])).catch(() => {});
  }, []);

  const today = opts && opts.today;
  const dates = rangeDates(range, today, months, custom);
  // With Invoiced shown (and a date range), Actual Cost, Hours and Rate Sheet
  // Value are moved back to line up with invoicing.
  const aligned = measures.includes('Billed') && (dates.from || dates.to);
  const costDates = aligned && measures.includes('Cost') ? costRange(dates) : null;
  const monthBack = MEASURES.filter((m) => (m.key === 'Hours' || m.key === 'RateSheet') && measures.includes(m.key));
  const rateDates = aligned && monthBack.length ? rateRange(dates) : null;

  const chooseRange = (key) => {
    if (key === 'months' && !months.from) {
      const r = presetRange(range === 'all' || range === 'days' ? 'last12' : range, today);
      setMonths({ from: r.from.slice(0, 7), to: r.to.slice(0, 7) });
    }
    if (key === 'days' && !custom.from) setCustom(dates.from ? dates : presetRange('last12', today));
    setRange(key);
  };

  // The screen's choices (what a saved report keeps), and applying them.
  const settings = () => ({ range, months, custom, period, groupBy, measures, job, pm, director, service, subService, category, person, overhead, jobState, taskState, axes, rankBy, topN });
  const setters = { range: setRange, months: setMonths, custom: setCustom, period: setPeriod, groupBy: setGroupBy, measures: setMeasures,
    job: setJob, pm: setPm, director: setDirector, service: setService, subService: setSubService, category: setCategory,
    person: setPerson, overhead: setOverhead, jobState: setJobState, taskState: setTaskState, axes: setAxes, rankBy: setRankBy, topN: setTopN };
  const applySettings = (s) => SETTING_KEYS.forEach((k) => { if (s[k] !== undefined) setters[k](s[k]); });

  const toggle = (list, setList, key) => setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  // Run with the given choices (the screen's, or a saved report's just applied).
  const run = async (s = settings()) => {
    const { from, to } = rangeDates(s.range, today, s.months, s.custom);
    const align = s.measures.includes('Billed') && (from || to);
    const cost = align && s.measures.includes('Cost') ? costRange({ from, to }) : { from: '', to: '' };
    const rate = align && (s.measures.includes('Hours') || s.measures.includes('RateSheet')) ? rateRange({ from, to }) : { from: '', to: '' };
    setLoading(true);
    setError('');
    try {
      const gb = s.groupBy.join('');
      const r = await api.getAnalyticsReport({
        from, to, period: s.period, groupBy: gb, job: s.job.trim(), pm: s.pm, director: s.director, service: s.service,
        subService: s.subService, category: s.category, person: s.person, overhead: s.overhead, jobState: s.jobState, taskState: s.taskState,
        costFrom: cost.from, costTo: cost.to, rateFrom: rate.from, rateTo: rate.to,
      });
      setResult({ rows: Array.isArray(r.rows) ? r.rows : [], ran: { period: s.period, groupBy: gb, from, to } });
    } catch (e) {
      setError((e && e.message) || 'Failed to run the report');
    } finally {
      setLoading(false);
    }
  };

  // Pivot: one row per (period, grouping) with a column per measure.
  const table = useMemo(() => {
    if (!result) return [];
    const map = new Map();
    for (const r of result.rows) {
      const k = rowKey(r);
      if (!map.has(k)) map.set(k, { ...r, values: {} });
      const row = map.get(k);
      row.values[r.metric] = (row.values[r.metric] || 0) + r.amount;
    }
    return Array.from(map.values()).sort((a, b) => (a.period || '').localeCompare(b.period || '')
      || a.jobNo.localeCompare(b.jobNo) || a.jobTaskNo.localeCompare(b.jobTaskNo)
      || a.category.localeCompare(b.category) || a.pmName.localeCompare(b.pmName) || a.directorName.localeCompare(b.directorName)
      || a.serviceName.localeCompare(b.serviceName) || a.subServiceName.localeCompare(b.subServiceName)
      || (a.resourceName || a.resourceNo).localeCompare(b.resourceName || b.resourceNo));
  }, [result]);

  const shown = MEASURES.filter((m) => measures.includes(m.key));
  const ran = result ? result.ran : null;
  const grouped = ran && ran.groupBy !== '';
  const totals = useMemo(() => {
    const t = {};
    table.forEach((r) => Object.entries(r.values).forEach(([k, v]) => { t[k] = (t[k] || 0) + v; }));
    return t;
  }, [table]);

  // Over-time chart data: measures summed across the grouping, per period.
  const overTime = useMemo(() => {
    if (!ran || ran.period === 'A') return null;
    const periods = Array.from(new Set(table.map((r) => r.period))).sort();
    const sum = (key) => periods.map((p) => table.filter((r) => r.period === p).reduce((s, r) => s + (r.values[key] || 0), 0));
    return { categories: periods.map((p) => ({ key: p, label: periodLabel(p, ran.period) })), sum };
  }, [ran, table]);

  // The measures on the charts, by axis (only those shown).
  const leftMs = shown.filter((m) => axes.left.includes(m.key));
  const rightMs = shown.filter((m) => axes.right.includes(m.key));
  const charted = [...leftMs, ...rightMs];
  // The Top N chart: Total reports that are grouped (period reports get the
  // By period chart instead).
  const totalChart = grouped && ran.period === 'A';
  const rankKey = (charted.find((m) => m.key === rankBy) || charted[0] || {}).key;

  // Top N groups (or all) for a Total report, ranked by one charted measure.
  const ranking = useMemo(() => {
    if (!grouped || !rankKey || ran.period !== 'A') return [];
    const groupLabel = (r) => [
      r.jobNo && `${r.jobNo}${r.jobDescription ? ` ${r.jobDescription}` : ''}`,
      r.jobTaskNo && `Task ${r.jobTaskNo}`,
      r.category,
      r.pmName && `PM ${r.pmName}`,
      r.directorName && `Dir. ${r.directorName}`,
      ran.groupBy.includes('S') && (r.serviceName || '(no service)'),
      ran.groupBy.includes('U') && (r.subServiceName || '(Unspecified)'),
      ran.groupBy.includes('P') && (r.resourceName || r.resourceNo || '(non-labor)'),
    ].filter(Boolean).join(' · ');
    const byGroup = new Map();
    for (const r of table) {
      const k = groupKey(r);
      if (!byGroup.has(k)) byGroup.set(k, { key: k, label: groupLabel(r), values: {} });
      const g = byGroup.get(k);
      Object.entries(r.values).forEach(([m, v]) => { g.values[m] = (g.values[m] || 0) + v; });
    }
    return Array.from(byGroup.values()).filter((g) => g.values[rankKey])
      .sort((a, b) => Math.abs(b.values[rankKey]) - Math.abs(a.values[rankKey]))
      .slice(0, topN === 'all' ? undefined : Number(topN));
  }, [grouped, table, rankKey, ran, topN]);


  const dims = ran ? [
    ran.period !== 'A' && { header: 'Period', key: 'periodLabel' },
    (ran.groupBy.includes('J') || ran.groupBy.includes('T')) && { header: 'Job', key: 'jobNo' },
    (ran.groupBy.includes('J') || ran.groupBy.includes('T')) && { header: 'Job Description', key: 'jobDescription' },
    (ran.groupBy.includes('J') || ran.groupBy.includes('T')) && { header: 'Job Status', key: 'jobStatusLabel' },
    (ran.groupBy.includes('J') || ran.groupBy.includes('T')) && !ran.groupBy.includes('M') && { header: 'Job Project Manager', key: 'jobProjectManager' },
    (ran.groupBy.includes('J') || ran.groupBy.includes('T')) && !ran.groupBy.includes('D') && { header: 'Job Director', key: 'jobDirector' },
    ran.groupBy.includes('T') && { header: 'Task', key: 'jobTaskNo' },
    ran.groupBy.includes('T') && { header: 'Task Description', key: 'jobTaskDescription' },
    ran.groupBy.includes('C') && { header: 'Task Line Type', key: 'category' },
    ran.groupBy.includes('M') && { header: 'Project Manager', key: 'pmName' },
    ran.groupBy.includes('D') && { header: 'Director', key: 'directorName' },
    ran.groupBy.includes('S') && { header: 'Service', key: 'serviceName' },
    ran.groupBy.includes('U') && { header: 'Sub-Service', key: 'subServiceName' },
    ran.groupBy.includes('P') && { header: 'Person', key: 'personLabel' },
  ].filter(Boolean) : [];
  const flat = (r) => ({
    ...r,
    periodLabel: periodLabel(r.period, ran.period),
    jobStatusLabel: r.jobNo ? `${JOB_STATUS[r.jobStatus] || r.jobStatus}${r.isOverhead ? ' (overhead)' : ''}` : '',
    personLabel: r.resourceName || r.resourceNo || '(non-labor)',
    serviceName: r.serviceName || (ran.groupBy.includes('S') ? '(none)' : ''),
    subServiceName: r.subServiceName || (ran.groupBy.includes('U') ? '(Unspecified)' : ''),
    ...r.values,
  });

  // The table rows in the chosen sort (measures first sort largest first).
  const isMeasure = (key) => MEASURES.some((m) => m.key === key);
  const sortValue = (r, key) => {
    if (isMeasure(key)) return r.values[key] || 0;
    if (key === 'periodLabel') return r.period || '';
    return String(flat(r)[key] || '');
  };
  const rows = !sort.key ? table : table.slice().sort((a, b) => {
    const x = sortValue(a, sort.key);
    const y = sortValue(b, sort.key);
    return (typeof x === 'number' ? x - y : x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' })) * sort.dir;
  });
  const sortBy = (key) => setSort((s0) => (s0.key === key ? { key, dir: -s0.dir } : { key, dir: isMeasure(key) ? -1 : 1 }));
  const sortTh = (key, label, className) => (
    <th key={key} className={`sortable${className ? ` ${className}` : ''}`} onClick={() => sortBy(key)}
      aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      {label}<span className="sort-indicator">{sort.key === key ? (sort.dir === 1 ? ICONS.sortUp : ICONS.sortDown) : ''}</span>
    </th>
  );

  const doExport = () => exportToExcel({
    name: 'JobReport',
    parts: [job.trim()],
    title: `Job report — ${ran.from ? displayDate(ran.from) : 'start'} to ${ran.to ? displayDate(ran.to) : 'today'}`,
    columns: [...dims, ...shown.map((m) => ({ header: m.label, key: m.key, type: m.unit === 'hours' ? 'hours' : 'money' }))],
    rows: rows.map(flat),
  });

  const choices = (all, list, none) => [
    { key: '', label: all },
    ...(none ? [{ key: '-', label: none }] : []),
    ...((opts && opts[list]) || []).map((x) => ({ key: x.no, label: x.name })),
  ];
  // Project Managers / Directors: current ones (not blocked, on a job that is
  // not closed) unless "Show all" was picked; the chosen one always stays listed.
  const peopleSel = (value, onChange, all, list, showAll, setShowAll) => {
    const items = (opts && opts[list]) || [];
    const visible = items.filter((x) => showAll || x.current !== false || x.no === value);
    return sel(value, (v) => (v === '__all' ? setShowAll(true) : onChange(v)), [
      { key: '', label: all },
      ...visible.map((x) => ({ key: x.no, label: x.name })),
      ...(visible.length < items.length ? [{ key: '__all', label: 'Show all…' }] : []),
    ]);
  };
  const monthSel = (value, onChange) => {
    const [y, m] = (value || '').split('-');
    const thisYear = Number((today || '').slice(0, 4)) || new Date().getFullYear();
    const years = Array.from({ length: 16 }, (_, i) => String(thisYear - 15 + i));
    return (
      <>
        {sel(m || '', (v) => onChange(`${y || thisYear}-${v}`), MONTH_NAMES.map((n, i) => ({ key: String(i + 1).padStart(2, '0'), label: n })))}
        {sel(y || '', (v) => onChange(`${v}-${m || '01'}`), years.map((v) => ({ key: v, label: v })))}
      </>
    );
  };

  // Saved reports (favorites).
  const loadFavorite = (id) => {
    setFavId(id);
    const f = favorites.find((x) => String(x.id) === String(id));
    if (!f) return;
    const s = { ...settings(), ...f.settings };
    applySettings(s);
    run(s);
  };
  const saveFavorite = async () => {
    const current = favorites.find((x) => String(x.id) === String(favId));
    const name = (window.prompt('Save this report as', current ? current.name : '') || '').trim();
    if (!name) return;
    try {
      const r = await api.saveReportFavorite(name, settings());
      const list = await api.getReportFavorites();
      setFavorites((list && list.favorites) || []);
      setFavId(r && r.id ? String(r.id) : '');
    } catch (e) {
      setError((e && e.message) || 'Failed to save the report');
    }
  };
  const deleteFavorite = async () => {
    const f = favorites.find((x) => String(x.id) === String(favId));
    if (!f || !window.confirm(`Delete the saved report "${f.name}"?`)) return;
    try {
      await api.deleteReportFavorite(f.id);
      setFavorites((list) => list.filter((x) => x.id !== f.id));
      setFavId('');
    } catch (e) {
      setError((e && e.message) || 'Failed to delete the saved report');
    }
  };
  const rangeNote = (r) => `${r.from ? displayDate(r.from) : 'start'} – ${r.to ? displayDate(r.to) : 'end'}`;
  const sel = (value, onChange, options) => (
    <select className="form-control" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 'auto', display: 'inline-block' }}>
      {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
  const checks = (items, list, setList) => items.map((it) => (
    <label key={it.key} className="report-check">
      <input type="checkbox" checked={list.includes(it.key)} onChange={() => toggle(list, setList, it.key)} />{it.label}
    </label>
  ));

  return (
    <WideSection fill maxWidth={1600}>
    <div style={{ padding: '8px 0 32px' }}>
      <PageHeader title="Reports" subtitle={opts && (opts.allJobs ? 'All jobs' : 'Jobs where you are Project Manager or Director')}
        actions={result && table.length > 0 && <ExportButton onExport={doExport} />} />
      <div className="report-controls">
        <div className="report-row">
          <span className="report-label">Saved</span>
          {sel(favId, loadFavorite, [{ key: '', label: favorites.length ? 'Saved reports…' : 'No saved reports' }, ...favorites.map((f) => ({ key: String(f.id), label: f.name }))])}
          <button type="button" className="btn btn-secondary btn-sm" onClick={saveFavorite} disabled={!opts}>{ICONS.favorite} Save…</button>
          {favId && <button type="button" className="btn btn-secondary btn-sm" onClick={deleteFavorite} title="Delete this saved report">{ICONS.delete}</button>}
        </div>
        <div className="report-row">
          <span className="report-label">Dates</span>
          {sel(range, chooseRange, RANGES)}
          {range === 'months' && (
            <>
              {monthSel(months.from, (v) => setMonths((x) => ({ ...x, from: v })))}
              <span>to</span>
              {monthSel(months.to, (v) => setMonths((x) => ({ ...x, to: v })))}
            </>
          )}
          {range === 'days' && (
            <>
              <input type="date" className="form-control" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} style={{ width: 150, display: 'inline-block' }} />
              <span>to</span>
              <input type="date" className="form-control" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} style={{ width: 150, display: 'inline-block' }} />
            </>
          )}
          <span className="report-label">By</span>
          {sel(period, setPeriod, PERIODS)}
        </div>
        <div className="report-row">
          <span className="report-label">Group by</span>
          {checks(GROUPS, groupBy, setGroupBy)}
        </div>
        <div className="report-row">
          <span className="report-label">Show</span>
          {checks(MEASURES, measures, setMeasures)}
        </div>
        {costDates && <div className="report-note">Actual Cost will be filtered back by half-month to align with invoicing ({rangeNote(costDates)}).</div>}
        {rateDates && <div className="report-note">{monthBack.map((m) => m.label).join(' and ')} will be filtered back by one month ({rangeNote(rateDates)}).</div>}
        <div className="report-row">
          <span className="report-label">Jobs</span>
          <input className="form-control" placeholder="Job no. (all)" value={job} onChange={(e) => setJob(e.target.value)} style={{ width: 130, display: 'inline-block' }} />
          {sel(overhead, setOverhead, [{ key: 'N', label: 'Non-overhead' }, { key: '*', label: 'Overhead and non-overhead' }, { key: 'Y', label: 'Overhead only' }])}
          {sel(jobState, setJobState, [{ key: '*', label: 'Open and closed jobs' }, { key: 'O', label: 'Open jobs' }, { key: 'C', label: 'Closed jobs' }])}
          {sel(taskState, setTaskState, [{ key: '*', label: 'Open and closed task lines' }, { key: 'O', label: 'Open task lines' }, { key: 'C', label: 'Closed task lines' }])}
        </div>
        <div className="report-row">
          <span className="report-label">Filter</span>
          {peopleSel(pm, setPm, 'All Project Managers', 'pms', showAllPms, setShowAllPms)}
          {peopleSel(director, setDirector, 'All Directors', 'directors', showAllDirectors, setShowAllDirectors)}
          {sel(service, setService, choices('All services', 'services', 'No service'))}
          {sel(subService, setSubService, choices('All sub-services', 'subServices', '(Unspecified)'))}
          {sel(category, setCategory, CATEGORIES)}
          {sel(person, setPerson, choices('All people', 'people'))}
          <button className="btn btn-primary btn-sm" onClick={() => run()} disabled={loading || !opts || !measures.length}>{loading ? 'Running…' : 'Run'}</button>
        </div>
      </div>
      <Banner kind="err" onClose={() => setError('')}>{error}</Banner>

      {result && !loading && (table.length === 0 ? <div style={{ padding: 16 }}>No data for these choices.</div> : (
        <>
          <div className="report-totals">
            {shown.map((m) => (
              <div key={m.key} className="report-total">
                <div className="report-total-label">{m.label}</div>
                <div className="report-total-value">{fmtMeasure(m.unit)(totals[m.key] || 0)}</div>
              </div>
            ))}
          </div>

          {(overTime || totalChart) && <ChartAxes items={shown} axes={axes} onChange={setAxes} />}
          {(overTime || totalChart) && !charted.length && (
            <div className="chart-empty" {...dropOnChart(axes, setAxes)}>Drag a measure here to chart it.</div>
          )}
          {overTime && charted.length > 0 && (
            <div {...dropOnChart(axes, setAxes)}>
              <ChartFrame label="By period chart" render={(height) => (
                <ComboChart title="By period" categories={overTime.categories} height={height || undefined}
                  left={chartSeries(leftMs, overTime.sum)} right={chartSeries(rightMs, overTime.sum)}
                  leftFormat={axisFormat(leftMs)} rightFormat={axisFormat(rightMs)} leftTitle={axisTitle(leftMs)} rightTitle={axisTitle(rightMs)} />
              )} />
            </div>
          )}
          {totalChart && charted.length > 0 && (
            <div {...dropOnChart(axes, setAxes)}>
              <div className="report-row" style={{ marginTop: 12 }}>
                <span className="report-label">Top</span>
                {sel(topN, setTopN, TOP_N)}
                <span>by</span>
                {sel(rankKey, setRankBy, charted)}
              </div>
              {ranking.length > 0 && (
                <ChartFrame label="Top groups chart" render={(height) => (
                  <ComboChart categories={ranking.map((g) => ({ key: g.key, label: g.label }))} longLabels height={height || undefined}
                    left={chartSeries(leftMs, (k) => ranking.map((g) => g.values[k] || 0))}
                    right={chartSeries(rightMs, (k) => ranking.map((g) => g.values[k] || 0))}
                    leftFormat={axisFormat(leftMs)} rightFormat={axisFormat(rightMs)} leftTitle={axisTitle(leftMs)} rightTitle={axisTitle(rightMs)} />
                )} />
              )}
            </div>
          )}

            <div className="report-table-wrap">
            <table className="data-table report-table">
              <thead>
                <tr>
                  {dims.map((d) => sortTh(d.key, d.header))}
                  {shown.map((m) => sortTh(m.key, m.label, 'num'))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const f = flat(r);
                  return (
                    <tr key={rowKey(r)}>
                      {dims.map((d) => <td key={d.key}>{f[d.key]}</td>)}
                      {shown.map((m) => <td key={m.key} className="num">{r.values[m.key] ? fmtMeasure(m.unit)(r.values[m.key]) : ''}</td>)}
                    </tr>
                  );
                })}
                <tr className="report-total-row">
                  {dims.length > 0 && <td colSpan={dims.length}>Total</td>}
                  {shown.map((m) => <td key={m.key} className="num">{fmtMeasure(m.unit)(totals[m.key] || 0)}</td>)}
                </tr>
              </tbody>
            </table>
            </div>
        </>
      ))}
    </div>
    </WideSection>
  );
}
